import { useEffect } from 'react'
import _ from 'lodash'

type InputEventDebounceOptions = { ms: number }

const debounceInputEvents = (input: HTMLInputElement, options: InputEventDebounceOptions) => {
  const { ms } = options
  const originalAddEventListener = input.addEventListener

  // replace this input's addEventListener so that any listeners attached can be hijacked
  input.addEventListener = function (
    type: string,
    listener: EventListener,
    options: AddEventListenerOptions,
  ) {
    if (type === 'input') {
      const debouncedListener = _.debounce(listener, ms, { trailing: true })

      return originalAddEventListener.call(this, type, debouncedListener, options)
    }

    // For other event types, proceed as normal
    return originalAddEventListener.call(this, type, listener, options)
  }
}

/**
 * Patches the input's addEventListener to debounce events.
 * @param inputId - The ID of the input element to debounce.
 */
export const useInputEventDebounce = (
  inputId: string,
  options?: Partial<InputEventDebounceOptions>,
) => {
  const { ms = 400 } = options || {} // defaults

  useEffect(() => {
    const input = document.getElementById(inputId)

    if (input?.tagName !== 'INPUT') {
      return console.warn(`useInputEventDebounce: inputId ${inputId} is not an input element`)
    }

    debounceInputEvents(input as HTMLInputElement, { ms })
  }, [inputId, ms])
}
